import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toggle-group';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import Breadcrumbs from '@Components/Breadcrumbs';
import Loader from '@Components/Loader';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import type { Control, UseFormGetValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import { evalKeys } from '@Hooks/useEvals';
import { AVAILABLE_TOOLS } from '@Constants/aiTools';
import type { EvalCase } from '@Types/eval';

// Schema shared between single-turn body and per-step body.
const ResultMatcherRowSchema = z.object({
  toolName: z.string(),
  fieldPath: z.string(),
  matcherType: z.enum(['equals', 'contains', 'regex', 'gte', 'lte', 'any', 'semantic']),
  value: z.string().optional(),
});

const BoMustExistRowSchema = z.object({
  key: z.string(),
  entityType: z.enum(['', 'fish', 'reservation', 'event', 'activity']).optional(),
  minCount: z.string().optional(),
  maxCount: z.string().optional(),
});

const AssertionFieldsSchema = z.object({
  containsText: z.string().optional(),
  forbiddenText: z.string().optional(),
  mustCall: z.array(z.string()).optional(),
  mustNotCall: z.array(z.string()).optional(),
  minStepsRequired: z.string().optional(),
  maxStepsAllowed: z.string().optional(),
  rubric: z.string().optional(),
  expectedAnswer: z.string().optional(),
  resultMatchers: z.array(ResultMatcherRowSchema).optional(),
  boMustExist: z.array(BoMustExistRowSchema).optional(),
  boMustNotExist: z.string().optional(), // newline-separated
});

const StepSchema = AssertionFieldsSchema.extend({
  userMessage: z.string().min(1, 'User message is required.'),
  // Chain steps use a JSON fallback for these advanced assertions to keep
  // the step UI from doubling in size. Single-turn uses the full builder.
  expectedMemoryJson: z.string().optional(),
  resultMatchersJson: z.string().optional(),
});

const FormSchema = z
  .object({
    mode: z.enum(['single', 'chain']),
    name: z.string().min(1, 'Name is required.'),
    description: z.string().optional(),
    weight: z.string().optional(),
    tags: z.string().optional(),
    containsPii: z.boolean(),
    // Single-turn fields:
    userMessage: z.string().optional(),
    containsText: z.string().optional(),
    forbiddenText: z.string().optional(),
    mustCall: z.array(z.string()).optional(),
    mustNotCall: z.array(z.string()).optional(),
    minStepsRequired: z.string().optional(),
    maxStepsAllowed: z.string().optional(),
    rubric: z.string().optional(),
    expectedAnswer: z.string().optional(),
    resultMatchers: z.array(ResultMatcherRowSchema).optional(),
    boMustExist: z.array(BoMustExistRowSchema).optional(),
    boMustNotExist: z.string().optional(),
    // Chain fields:
    steps: z.array(StepSchema).optional(),
  })
  .refine(
    (v) => {
      if (v.mode === 'single') return !!v.userMessage && v.userMessage.length > 0;
      return Array.isArray(v.steps) && v.steps.length > 0;
    },
    {
      message: 'Provide a user message (single) or at least one step (chain).',
      path: ['userMessage'],
    },
  );

type FormValues = z.infer<typeof FormSchema>;

type ResultMatcherRow = z.infer<typeof ResultMatcherRowSchema>;
type BoMustExistRow = z.infer<typeof BoMustExistRowSchema>;

const buildResultMatchersObject = (rows: ResultMatcherRow[] | undefined) => {
  if (!rows || rows.length === 0) return undefined;
  const out: Record<string, Record<string, any>> = {};
  for (const r of rows) {
    if (!r.toolName || !r.fieldPath) continue;
    out[r.toolName] = out[r.toolName] ?? {};
    if (r.matcherType === 'any') {
      out[r.toolName][r.fieldPath] = { type: 'any' };
      continue;
    }
    if (r.value == null || r.value === '') continue;
    let parsed: any = r.value;
    if (r.matcherType === 'gte' || r.matcherType === 'lte') parsed = Number(r.value);
    else if (r.matcherType === 'equals') {
      // Try JSON parse to support numbers/booleans/objects; fall back to string.
      try { parsed = JSON.parse(r.value); } catch { parsed = r.value; }
    }
    out[r.toolName][r.fieldPath] = { type: r.matcherType, value: parsed };
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

const buildExpectedMemoryObject = (rows: BoMustExistRow[] | undefined, mustNotText: string | undefined) => {
  const mustExist = (rows ?? [])
    .filter((r) => r.key && r.key.trim().length > 0)
    .map((r) => ({
      key: r.key.trim(),
      ...(r.entityType ? { entityType: r.entityType } : {}),
      ...(r.minCount ? { minCount: Number(r.minCount) } : {}),
      ...(r.maxCount ? { maxCount: Number(r.maxCount) } : {}),
    }));
  const mustNotExist = mustNotText
    ? mustNotText.split('\n').map((s) => s.trim()).filter(Boolean)
    : [];
  if (mustExist.length === 0 && mustNotExist.length === 0) return undefined;
  return {
    businessObjects: {
      ...(mustExist.length > 0 && { mustExist }),
      ...(mustNotExist.length > 0 && { mustNotExist }),
    },
  };
};

// Build the API payload's assertion/tool/step expectations from form values.
const buildAssertionPayload = (v: {
  containsText?: string;
  forbiddenText?: string;
  mustCall?: string[];
  mustNotCall?: string[];
  minStepsRequired?: string;
  maxStepsAllowed?: string;
  rubric?: string;
  expectedAnswer?: string;
  resultMatchers?: ResultMatcherRow[];
  boMustExist?: BoMustExistRow[];
  boMustNotExist?: string;
  // Chain-step JSON fallbacks
  resultMatchersJson?: string;
  expectedMemoryJson?: string;
}) => {
  const containsList = v.containsText
    ? v.containsText.split('\n').map((s) => s.trim()).filter(Boolean)
    : [];
  const forbiddenList = v.forbiddenText
    ? v.forbiddenText.split('\n').map((s) => s.trim()).filter(Boolean)
    : [];

  const out: any = {};
  if (containsList.length || forbiddenList.length) {
    out.assertions = {
      ...(containsList.length && { contains: containsList }),
      ...(forbiddenList.length && { forbiddenSubstrings: forbiddenList }),
    };
  }
  const expectedTools: any = {};
  if (v.mustCall && v.mustCall.length) expectedTools.mustCall = v.mustCall;
  if (v.mustNotCall && v.mustNotCall.length) expectedTools.mustNotCall = v.mustNotCall;

  // Builder-mode result matchers (single-turn) take precedence over JSON fallback.
  const builderResultMatchers = buildResultMatchersObject(v.resultMatchers);
  if (builderResultMatchers) {
    expectedTools.resultMatchers = builderResultMatchers;
  } else if (v.resultMatchersJson && v.resultMatchersJson.trim()) {
    try {
      const parsed = JSON.parse(v.resultMatchersJson);
      if (parsed && typeof parsed === 'object') expectedTools.resultMatchers = parsed;
    } catch { /* ignore — surfaced by JSON-textarea validation hint */ }
  }
  if (Object.keys(expectedTools).length) out.expectedTools = expectedTools;

  // Builder-mode expectedMemory (single-turn) vs JSON fallback (chain steps).
  const builderMemory = buildExpectedMemoryObject(v.boMustExist, v.boMustNotExist);
  if (builderMemory) {
    out.expectedMemory = builderMemory;
  } else if (v.expectedMemoryJson && v.expectedMemoryJson.trim()) {
    try {
      const parsed = JSON.parse(v.expectedMemoryJson);
      if (parsed && typeof parsed === 'object') out.expectedMemory = parsed;
    } catch { /* ignore */ }
  }

  if (v.minStepsRequired) out.minStepsRequired = Number(v.minStepsRequired);
  if (v.maxStepsAllowed) out.maxStepsAllowed = Number(v.maxStepsAllowed);
  if (v.rubric) out.rubric = v.rubric;
  if (v.expectedAnswer) out.expectedAnswer = v.expectedAnswer;
  return out;
};

const resultMatchersToRows = (rm: any): ResultMatcherRow[] => {
  if (!rm || typeof rm !== 'object') return [];
  const rows: ResultMatcherRow[] = [];
  for (const [toolName, fields] of Object.entries(rm)) {
    if (!fields || typeof fields !== 'object') continue;
    for (const [fieldPath, m] of Object.entries(fields as Record<string, any>)) {
      const matcherType = (m?.type ?? 'equals') as ResultMatcherRow['matcherType'];
      const value = matcherType === 'any'
        ? ''
        : (typeof m?.value === 'string' ? m.value : JSON.stringify(m?.value ?? ''));
      rows.push({ toolName, fieldPath, matcherType, value });
    }
  }
  return rows;
};

const expectedMemoryToBuilder = (em: any): { rows: BoMustExistRow[]; mustNotText: string } => {
  if (!em || typeof em !== 'object') return { rows: [], mustNotText: '' };
  const bo = em.businessObjects ?? {};
  const rows: BoMustExistRow[] = (bo.mustExist ?? []).map((r: any) => ({
    key: r?.key ?? '',
    entityType: r?.entityType ?? '',
    minCount: r?.minCount != null ? String(r.minCount) : '',
    maxCount: r?.maxCount != null ? String(r.maxCount) : '',
  }));
  const mustNotText = Array.isArray(bo.mustNotExist) ? bo.mustNotExist.join('\n') : '';
  return { rows, mustNotText };
};

// Build form defaults from a persisted case (edit mode).
const caseToFormValues = (c: EvalCase): FormValues => {
  const assertions = (c.assertions ?? {}) as { contains?: string[]; forbiddenSubstrings?: string[] };
  const expectedTools = (c.expectedTools ?? {}) as {
    mustCall?: string[];
    mustNotCall?: string[];
    resultMatchers?: any;
  };
  const isChain = Array.isArray(c.steps) && c.steps.length > 0;
  const memory = expectedMemoryToBuilder(c.expectedMemory);

  return {
    mode: isChain ? 'chain' : 'single',
    name: c.name,
    description: c.description ?? '',
    weight: c.weight != null ? String(c.weight) : '1',
    tags: (c.tags ?? []).join(', '),
    containsPii: c.containsPii ?? false,
    userMessage: isChain
      ? ''
      : (Array.isArray(c.inputMessages) && c.inputMessages.length > 0
          ? (c.inputMessages[0].content ?? '')
          : ''),
    containsText: (assertions.contains ?? []).join('\n'),
    forbiddenText: (assertions.forbiddenSubstrings ?? []).join('\n'),
    mustCall: expectedTools.mustCall ?? [],
    mustNotCall: expectedTools.mustNotCall ?? [],
    minStepsRequired: c.minStepsRequired != null ? String(c.minStepsRequired) : '',
    maxStepsAllowed: c.maxStepsAllowed != null ? String(c.maxStepsAllowed) : '',
    rubric: c.rubric ?? '',
    expectedAnswer: c.expectedAnswer ?? '',
    resultMatchers: resultMatchersToRows(expectedTools.resultMatchers),
    boMustExist: memory.rows,
    boMustNotExist: memory.mustNotText,
    steps: isChain
      ? (c.steps ?? []).map((s: any) => {
          const a = (s.assertions ?? {}) as { contains?: string[]; forbiddenSubstrings?: string[] };
          const t = (s.expectedTools ?? {}) as { mustCall?: string[]; mustNotCall?: string[]; resultMatchers?: any };
          return {
            userMessage: s.userMessage ?? '',
            containsText: (a.contains ?? []).join('\n'),
            forbiddenText: (a.forbiddenSubstrings ?? []).join('\n'),
            mustCall: t.mustCall ?? [],
            mustNotCall: t.mustNotCall ?? [],
            minStepsRequired: s.minStepsRequired != null ? String(s.minStepsRequired) : '',
            maxStepsAllowed: s.maxStepsAllowed != null ? String(s.maxStepsAllowed) : '',
            rubric: s.rubric ?? '',
            expectedAnswer: s.expectedAnswer ?? '',
            // Step-level memory + result matchers live as JSON fallbacks.
            resultMatchersJson: t.resultMatchers ? JSON.stringify(t.resultMatchers, null, 2) : '',
            expectedMemoryJson: s.expectedMemory ? JSON.stringify(s.expectedMemory, null, 2) : '',
          };
        })
      : [],
  };
};

const EMPTY_DEFAULTS: FormValues = {
  mode: 'single',
  name: '',
  description: '',
  weight: '1',
  tags: '',
  containsPii: false,
  userMessage: '',
  containsText: '',
  forbiddenText: '',
  mustCall: [],
  mustNotCall: [],
  minStepsRequired: '',
  maxStepsAllowed: '',
  rubric: '',
  expectedAnswer: '',
  resultMatchers: [],
  boMustExist: [],
  boMustNotExist: '',
  steps: [],
};

export default function CaseForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { suiteId, caseId } = useParams<{ suiteId: string; caseId?: string }>();
  const isEdit = !!caseId;

  const { data: existingCase, status: caseStatus } = useQuery<EvalCase>({
    queryKey: ['eval-case', caseId],
    queryFn: () => service.getEvalCase(caseId!),
    enabled: isEdit,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: EMPTY_DEFAULTS,
  });

  // Reset form once the existing case loads.
  useEffect(() => {
    if (isEdit && existingCase) {
      form.reset(caseToFormValues(existingCase));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingCase?.id]);

  const stepsField = useFieldArray({ control: form.control, name: 'steps' });
  const mode = form.watch('mode');

  const onSubmit = async (values: FormValues) => {
    if (!suiteId) return;
    form.clearErrors('root');

    const base: any = {
      name: values.name,
      description: values.description ? values.description : null,
      containsPii: values.containsPii,
      weight: values.weight ? Number(values.weight) : 1,
      tags: values.tags
        ? values.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : null,
    };

    let payload: any;
    if (values.mode === 'chain') {
      payload = {
        ...base,
        steps: (values.steps ?? []).map((s) => ({
          userMessage: s.userMessage,
          ...buildAssertionPayload(s),
        })),
        // Clear single-turn fields when switching to chain.
        inputMessages: [{ role: 'user', content: (values.steps ?? [])[0]?.userMessage ?? '' }],
        assertions: null,
        expectedTools: null,
        minStepsRequired: null,
        maxStepsAllowed: null,
        rubric: null,
        expectedAnswer: null,
      };
    } else {
      payload = {
        ...base,
        inputMessages: [{ role: 'user', content: values.userMessage }],
        steps: null,
        ...buildAssertionPayload(values),
      };
    }

    try {
      if (isEdit && caseId) {
        await service.updateEvalCase(caseId, payload);
        await queryClient.invalidateQueries({ queryKey: ['eval-case', caseId] });
      } else {
        await service.createEvalCase(suiteId, payload);
      }
      await queryClient.invalidateQueries({ queryKey: evalKeys.suite(suiteId) });
      toastSuccess(isEdit ? 'Case saved.' : 'Case added.');
      navigate(`/evals/${suiteId}`);
    } catch (error: any) {
      console.error(error);
      const isPublishedConflict = String(error?.message ?? '').includes('409');
      form.setError('root', {
        type: 'server',
        message: isPublishedConflict
          ? 'Suite is PUBLISHED — clone it to a draft before editing cases.'
          : (isEdit ? 'Failed to save case.' : 'Failed to create case.'),
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  if (isEdit && caseStatus === 'pending') return <Loader size="lg" />;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/evals' },
          { name: 'Evals', href: '/evals' },
          { name: 'Suite', href: `/evals/${suiteId}` },
          isEdit
            ? { name: 'Edit Case', href: `/evals/${suiteId}/cases/${caseId}/edit` }
            : { name: 'New Case', href: `/evals/${suiteId}/cases/new` },
        ]}
      />
      <div className="mt-8 mb-4">
        <Heading>{isEdit ? 'Edit Eval Case' : 'New Eval Case'}</Heading>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {rootError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <Heading level="h3">Basics</Heading>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mode</FormLabel>
                    <FormControl>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        value={field.value}
                        onValueChange={(v) => {
                          if (!v) return;
                          field.onChange(v);
                          if (v === 'chain' && (form.getValues('steps') ?? []).length === 0) {
                            stepsField.append({
                              userMessage: form.getValues('userMessage') ?? '',
                              containsText: '',
                              forbiddenText: '',
                              mustCall: [],
                              mustNotCall: [],
                              minStepsRequired: '',
                              maxStepsAllowed: '',
                              rubric: '',
                              expectedAnswer: '',
                              resultMatchersJson: '',
                              expectedMemoryJson: '',
                            });
                          }
                        }}
                      >
                        <ToggleGroupItem value="single" className="min-w-fit flex-none px-4">
                          Single turn
                        </ToggleGroupItem>
                        <ToggleGroupItem value="chain" className="min-w-fit flex-none px-4">
                          Chain
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </FormControl>
                    <FormDescription>
                      Chain mode sends a sequence of user messages turn-by-turn,
                      accumulating conversation history. Each step can have its
                      own assertions.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl><Textarea rows={2} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags</FormLabel>
                    <FormControl>
                      <Input placeholder="comma-separated, e.g. greeting, smoke-test" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Weight</FormLabel>
                      <FormControl><Input type="number" step="0.1" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="containsPii"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Contains PII</FormLabel>
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {mode === 'single' ? (
            <SingleTurnFields
              control={form.control}
              getValues={form.getValues}
              setValue={form.setValue}
              watch={form.watch}
            />
          ) : (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <Heading level="h3">Steps ({stepsField.fields.length})</Heading>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    stepsField.append({
                      userMessage: '',
                      containsText: '',
                      forbiddenText: '',
                      mustCall: [],
                      mustNotCall: [],
                      minStepsRequired: '',
                      maxStepsAllowed: '',
                      rubric: '',
                      expectedAnswer: '',
                      resultMatchersJson: '',
                      expectedMemoryJson: '',
                    })
                  }
                >
                  Add Step
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {stepsField.fields.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No steps yet. Add the first turn the user will send.
                  </p>
                )}
                {stepsField.fields.map((f, idx) => (
                  <StepCard
                    key={f.id}
                    index={idx}
                    total={stepsField.fields.length}
                    control={form.control}
                    getValues={form.getValues}
                    setValue={form.setValue}
                    watch={form.watch}
                    onRemove={() => stepsField.remove(idx)}
                    onMoveUp={idx > 0 ? () => stepsField.swap(idx, idx - 1) : undefined}
                    onMoveDown={
                      idx < stepsField.fields.length - 1
                        ? () => stepsField.swap(idx, idx + 1)
                        : undefined
                    }
                  />
                ))}
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="link" asChild>
                  <Link to={`/evals/${suiteId}`}>Cancel</Link>
                </Button>
                <Button type="submit" loading={form.formState.isSubmitting}>
                  {isEdit ? 'Save Changes' : 'Create Case'}
                </Button>
              </CardFooter>
            </Card>
          )}

          {mode === 'single' && (
            <Card>
              <CardFooter className="flex justify-end gap-2 pt-6">
                <Button variant="link" asChild>
                  <Link to={`/evals/${suiteId}`}>Cancel</Link>
                </Button>
                <Button type="submit" loading={form.formState.isSubmitting}>
                  {isEdit ? 'Save Changes' : 'Create Case'}
                </Button>
              </CardFooter>
            </Card>
          )}
        </form>
      </Form>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────

type ControlSubsetProps = {
  control: Control<FormValues>;
  getValues: UseFormGetValues<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  watch: UseFormWatch<FormValues>;
};

// Renders the single-turn input + the four shared assertion cards.
function SingleTurnFields({ control, getValues, setValue, watch }: ControlSubsetProps) {
  const toggle = (field: 'mustCall' | 'mustNotCall', value: string) => {
    const cur = getValues(field) ?? [];
    if (cur.includes(value)) {
      setValue(field, cur.filter((v) => v !== value));
    } else {
      setValue(field, [...cur, value]);
    }
  };
  const mustCall = watch('mustCall') ?? [];
  const mustNotCall = watch('mustNotCall') ?? [];

  return (
    <>
      <Card>
        <CardHeader><Heading level="h3">Input</Heading></CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={control}
            name="userMessage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>User message</FormLabel>
                <FormControl>
                  <Textarea rows={4} placeholder="What does the user ask the agent?" {...field} />
                </FormControl>
                <FormDescription>Single-turn input.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><Heading level="h3">Programmatic assertions</Heading></CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={control}
            name="containsText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Final text must contain</FormLabel>
                <FormControl><Textarea rows={3} placeholder="One per line" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="forbiddenText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Final text must NOT contain</FormLabel>
                <FormControl><Textarea rows={3} placeholder="One per line" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><Heading level="h3">Tool expectations</Heading></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Must call</p>
            <div className="flex flex-wrap gap-3">
              {AVAILABLE_TOOLS.map((t) => (
                <label key={`mc-${t}`} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={mustCall.includes(t)}
                    onCheckedChange={() => toggle('mustCall', t)}
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Must NOT call</p>
            <div className="flex flex-wrap gap-3">
              {AVAILABLE_TOOLS.map((t) => (
                <label key={`mnc-${t}`} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={mustNotCall.includes(t)}
                    onCheckedChange={() => toggle('mustNotCall', t)}
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><Heading level="h3">Step constraints</Heading></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <FormField
              control={control}
              name="minStepsRequired"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Min steps required</FormLabel>
                  <FormControl><Input type="number" min="0" {...field} /></FormControl>
                  <FormDescription>e.g. 1 = agent must call at least one tool.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="maxStepsAllowed"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Max steps allowed</FormLabel>
                  <FormControl><Input type="number" min="0" {...field} /></FormControl>
                  <FormDescription>e.g. 2 = answer in ≤2 tool calls.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><Heading level="h3">LLM-as-judge</Heading></CardHeader>
        <CardContent className="space-y-4">
          <FormField
            control={control}
            name="expectedAnswer"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expected answer (reference)</FormLabel>
                <FormControl><Textarea rows={4} {...field} /></FormControl>
                <FormDescription>If set, judge scores factual_accuracy/completeness/tone against this.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="rubric"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rubric (free-text)</FormLabel>
                <FormControl><Textarea rows={3} {...field} /></FormControl>
                <FormDescription>Used when no reference answer is provided.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </CardContent>
      </Card>

      <ResultMatchersCard control={control} watch={watch} />
      <BusinessObjectExpectationsCard control={control} />
    </>
  );
}

// ─── Result matchers card ─────────────────────────────────────
// Renders one editable row per (tool, field) pair. Tool dropdown is restricted
// to tools the user has added to mustCall (so result matchers only apply to
// expected calls). Matcher type 'any' hides the value column.
function ResultMatchersCard({
  control,
  watch,
}: {
  control: Control<FormValues>;
  watch: UseFormWatch<FormValues>;
}) {
  const rows = useFieldArray({ control, name: 'resultMatchers' });
  const mustCall = watch('mustCall') ?? [];
  const matcherTypes: ResultMatcherRow['matcherType'][] = [
    'equals',
    'contains',
    'regex',
    'gte',
    'lte',
    'any',
    'semantic',
  ];

  return (
    <Card>
      <CardHeader>
        <Heading level="h3">Tool result expectations</Heading>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-zinc-500">
          Assert against each tool's returned value (e.g. <code>searchMembers.totalHits ≥ 1</code>).
          Tool list is restricted to entries in <em>Must Call</em>.
        </p>
        {mustCall.length === 0 && (
          <p className="text-xs text-amber-600">
            Add at least one tool under "Tool expectations → Must call" to enable result matchers.
          </p>
        )}
        {rows.fields.length === 0 ? (
          <p className="text-xs text-zinc-500">No result matchers configured.</p>
        ) : (
          <div className="space-y-2">
            {rows.fields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-[1fr_1fr_120px_1fr_auto] items-end gap-2">
                <Controller
                  control={control}
                  name={`resultMatchers.${i}.toolName` as any}
                  render={({ field }) => (
                    <select
                      className="border-input bg-background flex h-9 rounded-md border px-2 text-sm"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <option value="">— tool —</option>
                      {mustCall.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  )}
                />
                <Controller
                  control={control}
                  name={`resultMatchers.${i}.fieldPath` as any}
                  render={({ field }) => (
                    <Input placeholder="field.path (e.g. totalHits)" value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
                <Controller
                  control={control}
                  name={`resultMatchers.${i}.matcherType` as any}
                  render={({ field }) => (
                    <select
                      className="border-input bg-background flex h-9 rounded-md border px-2 text-sm"
                      value={field.value ?? 'equals'}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      {matcherTypes.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  )}
                />
                <Controller
                  control={control}
                  name={`resultMatchers.${i}.value` as any}
                  render={({ field }) => {
                    const matcherType = watch(`resultMatchers.${i}.matcherType` as any) as string;
                    if (matcherType === 'any') {
                      return (
                        <Input
                          disabled
                          placeholder="(no value needed)"
                          value=""
                          onChange={() => undefined}
                        />
                      );
                    }
                    return <Input placeholder="value" value={field.value ?? ''} onChange={field.onChange} />;
                  }}
                />
                <Button type="button" variant="dangerLink" size="sm" onClick={() => rows.remove(i)}>
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={mustCall.length === 0}
          onClick={() =>
            rows.append({
              toolName: mustCall[0] ?? '',
              fieldPath: '',
              matcherType: 'equals',
              value: '',
            })
          }
        >
          Add result matcher
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── Business object expectations card ─────────────────────────
// mustExist: rows of {key, entityType, minCount, maxCount}
// mustNotExist: newline-separated keys
function BusinessObjectExpectationsCard({
  control,
}: {
  control: Control<FormValues>;
}) {
  const rows = useFieldArray({ control, name: 'boMustExist' });
  return (
    <Card>
      <CardHeader>
        <Heading level="h3">Business object expectations</Heading>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-zinc-500">
          Assert on the agent's chat-scoped working sets after the case runs.
          Useful for verifying the memory layer (e.g. <code>members</code> BO
          was persisted with at least 1 fish).
        </p>
        <div className="space-y-2">
          <p className="text-sm font-medium">Must exist</p>
          {rows.fields.length === 0 ? (
            <p className="text-xs text-zinc-500">No BO existence assertions.</p>
          ) : (
            rows.fields.map((f, i) => (
              <div key={f.id} className="grid grid-cols-[1fr_1fr_100px_100px_auto] items-end gap-2">
                <Controller
                  control={control}
                  name={`boMustExist.${i}.key` as any}
                  render={({ field }) => (
                    <Input placeholder="BO key (e.g. members)" value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
                <Controller
                  control={control}
                  name={`boMustExist.${i}.entityType` as any}
                  render={({ field }) => (
                    <select
                      className="border-input bg-background flex h-9 rounded-md border px-2 text-sm"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    >
                      <option value="">— any —</option>
                      <option value="fish">fish</option>
                      <option value="reservation">reservation</option>
                      <option value="event">event</option>
                      <option value="activity">activity</option>
                    </select>
                  )}
                />
                <Controller
                  control={control}
                  name={`boMustExist.${i}.minCount` as any}
                  render={({ field }) => (
                    <Input type="number" min="0" placeholder="min" value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
                <Controller
                  control={control}
                  name={`boMustExist.${i}.maxCount` as any}
                  render={({ field }) => (
                    <Input type="number" min="0" placeholder="max" value={field.value ?? ''} onChange={field.onChange} />
                  )}
                />
                <Button type="button" variant="dangerLink" size="sm" onClick={() => rows.remove(i)}>
                  Remove
                </Button>
              </div>
            ))
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => rows.append({ key: '', entityType: '', minCount: '', maxCount: '' })}
          >
            Add must-exist row
          </Button>
        </div>
        <Controller
          control={control}
          name="boMustNotExist"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Must NOT exist (one BO key per line)</FormLabel>
              <FormControl>
                <Textarea rows={2} placeholder="reservations" value={field.value ?? ''} onChange={field.onChange} />
              </FormControl>
              <FormDescription>
                Catches stale memory leaking between turns or surviving cleanup.
              </FormDescription>
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
}

// One step in a chain case. Same fields as single-turn assertions, scoped to a step index.
function StepCard({
  index,
  total,
  control,
  getValues,
  setValue,
  watch,
  onRemove,
  onMoveUp,
  onMoveDown,
}: ControlSubsetProps & {
  index: number;
  total: number;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}) {
  const base = `steps.${index}` as const;
  const toggleStepTool = (which: 'mustCall' | 'mustNotCall', value: string) => {
    const path = `${base}.${which}` as const;
    const cur = (getValues(path as any) ?? []) as string[];
    if (cur.includes(value)) {
      setValue(path as any, cur.filter((v) => v !== value) as any);
    } else {
      setValue(path as any, [...cur, value] as any);
    }
  };
  const mustCall = (watch(`${base}.mustCall` as any) as string[]) ?? [];
  const mustNotCall = (watch(`${base}.mustNotCall` as any) as string[]) ?? [];

  return (
    <div className="rounded-md border p-4 space-y-4 bg-zinc-50/50 dark:bg-zinc-900/30">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline">Step {index + 1} of {total}</Badge>
        </div>
        <div className="flex gap-1">
          <Button type="button" variant="link" size="sm" onClick={onMoveUp} disabled={!onMoveUp}>↑</Button>
          <Button type="button" variant="link" size="sm" onClick={onMoveDown} disabled={!onMoveDown}>↓</Button>
          <Button type="button" variant="dangerLink" size="sm" onClick={onRemove}>Remove</Button>
        </div>
      </div>

      <Controller
        control={control}
        name={`${base}.userMessage` as any}
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>User message</FormLabel>
            <FormControl>
              <Textarea rows={3} placeholder="What does the user say on this turn?" {...field} />
            </FormControl>
            {fieldState.error && <FormMessage>{fieldState.error.message}</FormMessage>}
          </FormItem>
        )}
      />

      <details>
        <summary className="cursor-pointer text-sm font-medium text-zinc-600">
          Step-level assertions (optional)
        </summary>
        <div className="space-y-4 mt-3">
          <Controller
            control={control}
            name={`${base}.containsText` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Final text must contain</FormLabel>
                <FormControl><Textarea rows={2} placeholder="One per line" {...field} /></FormControl>
              </FormItem>
            )}
          />
          <Controller
            control={control}
            name={`${base}.forbiddenText` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Final text must NOT contain</FormLabel>
                <FormControl><Textarea rows={2} placeholder="One per line" {...field} /></FormControl>
              </FormItem>
            )}
          />
          <div>
            <p className="text-sm font-medium mb-2">Must call</p>
            <div className="flex flex-wrap gap-3">
              {AVAILABLE_TOOLS.map((t) => (
                <label key={`s${index}-mc-${t}`} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={mustCall.includes(t)}
                    onCheckedChange={() => toggleStepTool('mustCall', t)}
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Must NOT call</p>
            <div className="flex flex-wrap gap-3">
              {AVAILABLE_TOOLS.map((t) => (
                <label key={`s${index}-mnc-${t}`} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={mustNotCall.includes(t)}
                    onCheckedChange={() => toggleStepTool('mustNotCall', t)}
                  />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-4">
            <Controller
              control={control}
              name={`${base}.minStepsRequired` as any}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Min steps required</FormLabel>
                  <FormControl><Input type="number" min="0" {...field} /></FormControl>
                </FormItem>
              )}
            />
            <Controller
              control={control}
              name={`${base}.maxStepsAllowed` as any}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Max steps allowed</FormLabel>
                  <FormControl><Input type="number" min="0" {...field} /></FormControl>
                </FormItem>
              )}
            />
          </div>
          <Controller
            control={control}
            name={`${base}.expectedAnswer` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expected answer (reference)</FormLabel>
                <FormControl><Textarea rows={2} {...field} /></FormControl>
              </FormItem>
            )}
          />
          <Controller
            control={control}
            name={`${base}.rubric` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rubric (free-text)</FormLabel>
                <FormControl><Textarea rows={2} {...field} /></FormControl>
              </FormItem>
            )}
          />
          <Controller
            control={control}
            name={`${base}.resultMatchersJson` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Result matchers (JSON)</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    placeholder='{ "searchMembers": { "totalHits": { "type": "gte", "value": 1 } } }'
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Per-tool result-field assertions for this step. Builder UI lives on single-turn cases; chain steps use JSON.
                </FormDescription>
              </FormItem>
            )}
          />
          <Controller
            control={control}
            name={`${base}.expectedMemoryJson` as any}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Expected memory (JSON)</FormLabel>
                <FormControl>
                  <Textarea
                    rows={4}
                    placeholder='{ "businessObjects": { "mustExist": [{ "key": "members", "entityType": "fish", "minCount": 1 }] } }'
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  After this step, the agent's business-object + tool-memory state should match this shape.
                </FormDescription>
              </FormItem>
            )}
          />
        </div>
      </details>
    </div>
  );
}
